import { type z } from 'zod';

import { IdsParamSchema } from '../../common/ids-param/ids-param';
import { makeListQuery } from '../../common/list-query/list-query';

import { MediaKindSchema } from '../media-kind/media-kind';

/**
 * The admin library query (N7). `kind` is **required**, unlike the member list's
 * optional filter: the platform scope and the kind are applied together in one
 * repository method, because a query that selected every ownerless asset
 * regardless of kind would be a list nobody asked for and a widening waiting to
 * happen.
 *
 * There is no `status` filter: the admin library shows what is *usable*, and a
 * `pending` reservation is an upload in flight rather than an asset. The
 * repository scopes to `ready` below the filter layer, so no parameter can widen
 * it.
 */
export const PlatformMediaListQuerySchema = makeListQuery({
  kind: MediaKindSchema,
  /**
   * Resolve-by-ids for `MediaPicker` — the same contract every `ServerSelect`
   * already has (`IdsParamSchema`).
   *
   * A `media`-controlled setting stores one uuid, and the picker has to render
   * that asset — its thumbnail, its dimensions and its size — without knowing
   * which page of the library it happens to be on. Without this the stored value
   * could only be *minted* (`GET /media/:id/url` returns a url and nothing else),
   * so a logo would show its picture and lose its facts on every reload.
   *
   * It cannot widen anything: the platform scope, the kind and the `ready` scope
   * are all applied below the filter layer.
   */
  ids: IdsParamSchema.optional(),
});

export type PlatformMediaListQuery = z.infer<typeof PlatformMediaListQuerySchema>;
